import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/**
 * Checks if a user has admin status by calling the is_admin RPC function
 */
export const checkAdminStatus = async (userId: string): Promise<boolean> => {
  if (!userId) return false;
  
  try {
    const { data, error } = await supabase.rpc('is_admin', {
      user_id: userId
    });

    if (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
    
    return !!data;
  } catch (error) {
    console.error('Unexpected error checking admin status:', error);
    return false;
  }
};

/**
 * Finds a user ID by email address
 */
export const findUserIdByEmail = async (email: string): Promise<string | null> => {
  try {
    // Use a simpler typing approach to avoid deep type inference
    const result = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .maybeSingle();
    
    const { data, error } = result;
    
    if (error) {
      console.error('Error finding user by email:', error);
      return null;
    }
    
    return data?.id || null;
  } catch (error) {
    console.error('Unexpected error finding user by email:', error);
    return null;
  }
};

/**
 * Updates a user's admin status directly in the profiles table
 */
export const setUserAdminStatus = async (email: string): Promise<boolean> => {
  try {
    // First we need to find the user in the profiles table by email
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('email', email)
      .maybeSingle();
    
    if (profileError || !profileData) {
      console.error('Error finding user profile by email:', profileError);
      toast({
        title: "Error finding user",
        description: `Could not find user profile with email ${email}`,
        variant: "destructive",
      });
      return false;
    }
    
    const userId = profileData.id;
    
    // Now update the profile
    const { error } = await supabase
      .from('profiles')
      .update({ role: 'admin' })
      .eq('id', userId);
    
    if (error) {
      console.error('Error updating admin status:', error);
      toast({
        title: "Error updating admin status",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
    
    toast({
      title: "Success",
      description: `${email} has been granted admin privileges`,
    });
    return true;
  } catch (error) {
    console.error('Unexpected error setting admin status:', error);
    toast({
      title: "Error",
      description: "An unexpected error occurred",
      variant: "destructive",
    });
    return false;
  }
};

/**
 * Creates default workouts for new users
 */
export const createDefaultWorkouts = async (userId: string): Promise<void> => {
  try {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfterTomorrow = new Date(today);
    dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
    
    const defaultWorkouts = [
      {
        user_id: userId,
        title: "Prompt Engineering Basics",
        description: "Learn the fundamentals of crafting effective prompts",
        date_assigned: today.toISOString(),
        time_assigned: "09:00",
      },
      {
        user_id: userId,
        title: "Chain-of-Thought Practice",
        description: "Improve reasoning by using step-by-step thinking",
        date_assigned: tomorrow.toISOString(),
        time_assigned: "10:30",
      },
      {
        user_id: userId,
        title: "Advanced Context Setting",
        description: "Master techniques for providing clear context to AI models",
        date_assigned: dayAfterTomorrow.toISOString(),
        time_assigned: "14:00",
      }
    ];

    for (const workout of defaultWorkouts) {
      const { error } = await supabase.from('workouts').insert(workout);
      if (error) console.error("Error creating default workout:", error);
    }

    const { error: streakError } = await supabase.from('user_streaks').insert({
      user_id: userId,
      current_streak: 0,
      max_streak: 0,
    });
    
    if (streakError) console.error("Error creating user streak:", streakError);
  } catch (error) {
    console.error("Error creating default workouts:", error);
  }
};
